import { useEffect, useRef } from "react";
import { keyCodeToKeyName } from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { EventType } from "src/store/api/generatedApi";

export const useRecordingRelease = ({
  liftHotkey,
  dropHotkey,
  recordingEventId,
  recordingEventType,
  onRelease,
  onInterrupt,
}: Readonly<{
  liftHotkey: string;
  dropHotkey: string;
  recordingEventId: string | null;
  recordingEventType: EventType | null;
  onRelease: (eventType: EventType) => void;
  onInterrupt: () => void;
}>): void => {
  const liftHotkeyRef = useRef(liftHotkey);
  liftHotkeyRef.current = liftHotkey;
  const dropHotkeyRef = useRef(dropHotkey);
  dropHotkeyRef.current = dropHotkey;
  const recordingEventIdRef = useRef(recordingEventId);
  recordingEventIdRef.current = recordingEventId;
  const recordingEventTypeRef = useRef(recordingEventType);
  recordingEventTypeRef.current = recordingEventType;
  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;
  const onInterruptRef = useRef(onInterrupt);
  onInterruptRef.current = onInterrupt;

  const heldEventTypeRef = useRef<EventType | null>(null);
  useEffect(() => {
    heldEventTypeRef.current =
      recordingEventId === null ? null : recordingEventTypeRef.current;
  }, [recordingEventId]);

  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      if (recordingEventIdRef.current === null) return;
      const released = keyCodeToKeyName(event.code);
      let eventType: EventType;
      if (released === liftHotkeyRef.current) {
        eventType = "lift";
      } else if (released === dropHotkeyRef.current) {
        eventType = "drop";
      } else {
        return;
      }
      if (
        heldEventTypeRef.current !== null &&
        eventType !== heldEventTypeRef.current
      ) {
        return;
      }
      event.preventDefault();
      onReleaseRef.current(eventType);
    };
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const stopIfRecording = () => {
      if (recordingEventIdRef.current !== null) {
        onInterruptRef.current();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) stopIfRecording();
    };
    window.addEventListener("blur", stopIfRecording);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", stopIfRecording);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopIfRecording();
    };
  }, []);
};
